import { useMemo, useState } from "react";
import classNames from "classnames";
import { CloseSelectItem } from "@components/tasks/TaskView/Cells/CloseSelectItem";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import { Picker } from "@design-system/Picker/Picker.lib";
import fieldStyles from "./TaskDrawerFields.module.scss";

interface NullableSelectFieldProps {
    label: string;
    placeholder: string;
    noneLabel: string;
    searchPlaceholder: string;
    emptyLabel: string;
    items: Array<{
        id: string;
        name: string;
        entityId?: number;
        startDate?: string | null;
        endDate?: string | null;
    }>;
    value: string | null | undefined;
    onValueChange: (value: string | null) => void;
}

const matchesSearch = ({ text, search }: { text: string; search: string }) =>
    text.toLowerCase().includes(search);

export const NullableSelectField = ({
    label,
    placeholder,
    noneLabel,
    searchPlaceholder,
    emptyLabel,
    items,
    value,
    onValueChange,
}: NullableSelectFieldProps) => {
    const [search, setSearch] = useState("");
    const normalizedSearch = search.trim().toLowerCase();
    const selectedItem =
        value != null ? items.find((item) => item.id === value) : undefined;

    const visibleItems = useMemo(
        () =>
            normalizedSearch
                ? items.filter((item) =>
                      matchesSearch({
                          text: item.name,
                          search: normalizedSearch,
                      }),
                  )
                : items,
        [items, normalizedSearch],
    );
    const isNoneVisible =
        !normalizedSearch ||
        matchesSearch({ text: noneLabel, search: normalizedSearch });

    return (
        <DescriptionListItem label={label}>
            <div className={fieldStyles.detailSelectWrapper}>
                <Picker.Select
                    searchable
                    value={selectedItem?.id ?? null}
                    onValueChange={onValueChange}
                    onInputValueChange={setSearch}
                >
                    <Picker.ButtonTrigger
                        render={
                            <Picker.Button
                                variant="primary"
                                size="md"
                                block={false}
                                className={classNames(
                                    fieldStyles.detailSelectTrigger,
                                    {
                                        [fieldStyles.emptyValue]: !value,
                                    },
                                )}
                            />
                        }
                        value={selectedItem?.name}
                        placeholder={placeholder}
                    />
                    <Picker.Popup sideOffset={4}>
                        <Picker.Search placeholder={searchPlaceholder} />
                        <Picker.List>
                            {isNoneVisible && (
                                <Picker.Item
                                    onSelect={() => onValueChange(null)}
                                >
                                    {noneLabel}
                                </Picker.Item>
                            )}
                            {visibleItems.map((item) => (
                                <CloseSelectItem
                                    key={item.id}
                                    id={item.id}
                                    name={item.name}
                                    entityId={item.entityId}
                                    startDate={item.startDate}
                                    endDate={item.endDate}
                                />
                            ))}
                            {!isNoneVisible && visibleItems.length === 0 && (
                                <Picker.Empty>{emptyLabel}</Picker.Empty>
                            )}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </div>
        </DescriptionListItem>
    );
};
